import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAudiences } from '../../hooks/useAudiences';
import { useAudience } from '../../hooks/useAudience';
import { useCategory } from '../../hooks/useCategory';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import AudienceCategoriesView from './AudienceCategoriesView';
import AudienceStickyHeader from './AudienceStickyHeader';
import { CountryState, PlatformState, SearchState, CategoryState, DialogState, AudienceViewMode, LayoutState } from '../../types';
import { CountryItem, AudiencePlatformItem } from '@digiseg-labs/api-client';
import { Box } from '@radix-ui/themes';
import AudienceHeroSection from './AudienceHeroSection';
import GoToTopButton from './GoToTopButton';
import AudienceRecommendationModal from '../AudienceRecommendationModal';
import ShareMenu from './ShareMenu';
import {
  clearInvalidStoredSelections,
  getStoredCountryCode,
  getStoredPlatformCode,
  getWelcomeDismissed,
  setStoredCountryCode,
  setStoredPlatformCode,
  setWelcomeDismissed,
} from '../../utils/prefs';
const isBrowser = typeof window !== 'undefined';
const SEARCH_DEBOUNCE_MS = 300;
const INTERSECTION_OBSERVER_MARGIN = '0px 0px 200px 0px';

const parsePageSize = (value?: string | null, fallback = 10) => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseSort = (value?: string | null) => (value === 'desc' ? 'desc' : 'asc');

const parseLabelsParam = (value?: string | null): AudienceViewMode => {
  if (!value) return [];
  const seen = new Set<string>();
  return value
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .map((entry) => {
      if (entry === 'interest') return 'Interest';
      if (entry === 'demographic') return 'Demographic';
      return null;
    })
    .filter((entry): entry is AudienceViewMode[number] => Boolean(entry))
    .filter((entry) => {
      if (seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
};

const areViewModesEqual = (a: AudienceViewMode, b: AudienceViewMode) =>
  JSON.stringify(a) === JSON.stringify(b);


const AudienceListApp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  const initialUrlState = useMemo(() => {
    if (!isBrowser) return {};
    return {
      country: searchParams.get('country') || undefined,
      platform: searchParams.get('platform') || undefined,
      category: searchParams.get('category') || undefined,
      audience: searchParams.get('audience') || undefined,
      filter: searchParams.get('filter') || undefined,
      labels: searchParams.get('labels') || undefined,
      pagesize: searchParams.get('pagesize') || undefined,
      sort: searchParams.get('sort') || undefined,
      pageafter: searchParams.get('pageafter') || undefined,
      countrySelector: searchParams.get('countrySelector') || undefined,
    };
  }, [searchParams]);

  const pathCountry = useMemo(() => {
    if (!location.pathname) return undefined;
    const segments = location.pathname.split('/').filter(Boolean);
    const normalized = segments.map((segment) => segment.toLowerCase());
    const listIndex = normalized.findIndex((segment) => segment === 'audience-list' || segment === 'audiences');
    if (listIndex === -1) return undefined;
    const candidate = segments[listIndex + 1];
    return candidate ? candidate.toUpperCase() : undefined;
  }, [location.pathname]);

  const urlCountryCode = useMemo(() => {
    const queryCountry = searchParams.get('country');
    return queryCountry ? queryCountry.toUpperCase() : undefined;
  }, [searchParams]);
  const initialCountryCode = (initialUrlState.country ?? pathCountry)?.toUpperCase();
  const { countries, platforms } = useTaxonomy();

  const isUserChangingCountryRef = useRef(false);
  const [country, setCountry] = useState<CountryItem | undefined>(() => {
    if (!isBrowser) return undefined;
    const urlCountry = initialUrlState.country?.toUpperCase();
    if (urlCountry && countries && countries.length > 0) {
      return countries.find((c) => c.code.toUpperCase() === urlCountry);
    }
    const saved = getStoredCountryCode();
    if (saved && countries && countries.length > 0) {
      return countries.find((c) => c.code === saved);
    }
    return undefined;
  });
  const [platform, setPlatform] = useState<AudiencePlatformItem | undefined>(() => {
    if (!isBrowser) return undefined;
    const urlPlatform = initialUrlState.platform;
    if (urlPlatform && platforms && platforms.length > 0) {
      return platforms.find((p) => p.code === urlPlatform);
    }
    const saved = getStoredPlatformCode();
    if (saved && platforms && platforms.length > 0) {
      return platforms.find((p) => p.code === saved);
    }
    return undefined;
  });
  const [filter, setFilter] = useState('');
  const [audienceViewMode, setAudienceViewMode] = useState<AudienceViewMode>(() =>
    parseLabelsParam(initialUrlState.labels),
  );
  const [pageafter, setPageafter] = useState<string | undefined>();
  const [pagesize, setPagesize] = useState(() => {
    return parsePageSize(initialUrlState.pagesize, 10);
  });
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialUrlState.filter ?? '');
  const [sort, setSort] = useState<string | undefined>(parseSort(initialUrlState.sort));
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [activeCategoryCode, setActiveCategoryCode] = useState<string | null>(
    initialUrlState.audience ? null : (initialUrlState.category ?? null),
  );
  const [openAudienceCode, setOpenAudienceCode] = useState<string | null>(initialUrlState.audience ?? null);
  const [isCompact, setIsCompact] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  const hasAppliedInitialCountry = useRef(false);
  const hasAppliedInitialPlatform = useRef(false);
  const prevUrlCountryRef = useRef<string | null>(null);
  const prevUrlPlatformRef = useRef<string | null>(null);
  const pendingScrollToCategoryRef = useRef<string | null>(initialUrlState.pageafter ?? null);
  const stickyVisibilityRef = useRef(false);
  const stickyThresholdRef = useRef<number>(0);
  const countryState: CountryState = { country, setCountry, countries};
  const platformState: PlatformState = { platform, setPlatform, platforms };
  const headerSentinelRef = useRef<HTMLDivElement | null>(null);
  const isSearching = searchQuery.trim().toLowerCase().length >= 2;
  const lastAnchorBeforeFilterRef = useRef<string | null>(null);

  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const forceCountrySelectorRef = useRef(initialUrlState.countrySelector === 'open');

  const { categories, meta, isLoading } = useAudiences({
    country,
    platform,
    filter,
    pageafter,
    pagesize,
    labels: audienceViewMode,
    sort: sort as any,
  });

  const categoryFromList = useMemo(() => {
    if (!activeCategoryCode) return undefined;
    return categories.find((category) => category.code === activeCategoryCode);
  }, [activeCategoryCode, categories]);

  const {
    category: sharedCategory,
    isLoading: isLoadingCategory,
  } = useCategory({
    categoryCode: activeCategoryCode ?? undefined,
    platform,
    country,
    enabled: !!activeCategoryCode && !categoryFromList,
  });

  const activeCategory = categoryFromList ?? sharedCategory;
  const renderedCategories = useMemo(() => {
    if (!activeCategoryCode) return categories;
    if (categoryFromList) return categories;
    if (sharedCategory) return [sharedCategory];
    return [];
  }, [activeCategoryCode, categories, categoryFromList, sharedCategory]);
  const hasVisibleAudiences = renderedCategories.some(
    (category) => (category.audiences?.length ?? 0) > 0,
  );
  const shouldShowInitialSkeleton =
    (!categories.length && isLoading) || (!!activeCategoryCode && !activeCategory && isLoadingCategory);

  const audienceFromList = useMemo(() => {
    if (!openAudienceCode) return undefined;
    for (const category of categories) {
      const match = category.audiences?.find((audience) => audience.code === openAudienceCode);
      if (match) return match;
    }
    return undefined;
  }, [categories, openAudienceCode]);

  const { audience: sharedAudience } = useAudience({
    audienceCode: openAudienceCode ?? undefined,
    platform,
    country,
    enabled: !!openAudienceCode && !audienceFromList,
  });

  const resolvedAudience = audienceFromList ?? sharedAudience;
  const shouldRenderStandaloneModal = !!openAudienceCode && !audienceFromList;

    useEffect(() => {
    if (!isBrowser) return;
    const handleScroll = () => {
      const threshold = stickyThresholdRef.current;
      const nextState = window.scrollY > threshold;
      if (stickyVisibilityRef.current !== nextState) {
        stickyVisibilityRef.current = nextState;
        setShowStickyHeader(nextState);
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    const handleResize = () => {
      const width = window.innerWidth;
      setIsCompact(width < 640);
      setIsMedium(width >= 640 && width < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
useEffect(() => {
  const timer = setTimeout(() => {
    setFilter(searchQuery);
    setPageafter(undefined);
  }, SEARCH_DEBOUNCE_MS);

  return () => clearTimeout(timer);
}, [searchQuery]);

  const searchParamsString = searchParams.toString();

  useEffect(() => {
    const params = new URLSearchParams(searchParamsString);
    const nextFilter = params.get('filter') ?? '';
    setSearchQuery((prev) => {
      if (prev === nextFilter) return prev;
      setPageafter(undefined);
      return nextFilter;
    });

    const nextAudience = params.get('audience');
    setOpenAudienceCode((prev) => ((nextAudience ?? null) === prev ? prev : nextAudience));

    const nextCategory = params.get('category');
    setActiveCategoryCode((prev) => {
      if (nextAudience) {
        return prev;
      }
      if ((nextCategory ?? null) === prev) return prev;
      setPageafter(undefined);
      return nextCategory;
    });

    const nextPagesize = parsePageSize(params.get('pagesize'), 10);
    setPagesize((prev) => {
      if (prev === nextPagesize) return prev;
      setPageafter(undefined);
      return nextPagesize;
    });

    const nextSort = parseSort(params.get('sort'));
    setSort((prev) => {
      if (prev === nextSort) return prev;
      setPageafter(undefined);
      return nextSort;
    });

    const nextLabels = parseLabelsParam(params.get('labels'));
    setAudienceViewMode((prev) => {
      if (areViewModesEqual(nextLabels, prev)) return prev;
      setPageafter(undefined);
      return nextLabels;
    });
  }, [searchParamsString]);

  useEffect(() => {
    if (!countries.length && !platforms.length) return;
    clearInvalidStoredSelections(countries, platforms);
  }, [countries, platforms]);

    const buildShareUrl = useCallback(
    ({ categoryCode, audienceCode, includeCountry, includePlatform, searchQuery: shareSearchQuery }: {
      categoryCode?: string;
      audienceCode?: string;
      includeCountry?: boolean;
      includePlatform?: boolean;
      searchQuery?: string;
    }) => {
      if (!isBrowser) return '';
      const params = new URLSearchParams();
      if (includeCountry && country?.code) {
        params.set('country', country.code);
      }
      if (includePlatform && platform?.code) {
        params.set('platform', platform.code);
      }
      if (categoryCode) {
        params.set('category', categoryCode);
      }
      if (audienceCode) {
        params.set('audience', audienceCode);
      }
      if (shareSearchQuery) {
        params.set('filter', shareSearchQuery);
      }
      if (audienceViewMode.length > 0) {
        params.set('labels', audienceViewMode.join(','));
      }
      params.set('pagesize', pagesize.toString());
      params.set('sort', sort || 'asc');
      const base = `${window.location.origin}${window.location.pathname}`;
      const search = params.toString();
      return search ? `${base}?${search}` : base;
    },
    [country?.code, platform?.code, audienceViewMode, pagesize, sort],
  );

  const handleAudienceModalOpenChange = useCallback((audienceCode: string, open: boolean) => {
  setOpenAudienceCode(open ? audienceCode : null);
}, []);
const handleGoToTop = useCallback(() => {
  if (!isBrowser) return;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, []);

  const loadMoreSentinelRef = useCallback(
  (node: HTMLDivElement | null) => {
    if (loadMoreObserverRef.current) {
      loadMoreObserverRef.current.disconnect();
      loadMoreObserverRef.current = null;
    }
    if (!node || !meta?.page?.rangeTruncated || typeof IntersectionObserver === 'undefined') return;
    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setPageafter(meta?.page?.last_cursor);
          }
        });
      },
      { rootMargin: INTERSECTION_OBSERVER_MARGIN },
    );
    loadMoreObserverRef.current.observe(node);
  },
  [ meta?.page?.rangeTruncated, meta?.page?.last_cursor],
);


  const clearCategoryFilter = () => {
      if (lastAnchorBeforeFilterRef.current) {
        pendingScrollToCategoryRef.current = lastAnchorBeforeFilterRef.current;
      }
      lastAnchorBeforeFilterRef.current = null;
      setActiveCategoryCode(null);
      setSearchQuery('');
      // Clear backend filter immediately to keep results in sync.
      setFilter('');
      setPageafter(undefined);
    };

    useEffect(() => {
    if (!isBrowser) return;
    const dismissed = getWelcomeDismissed();
    const savedCountry = getStoredCountryCode();

    if (initialUrlState.countrySelector === 'open' && !initialCountryCode) {
      forceCountrySelectorRef.current = true;
      setShowWelcomeDialog(true);
      return;
    }

    if (!dismissed && !savedCountry && !initialCountryCode) {
      setShowWelcomeDialog(true);
    }
  }, [initialUrlState.countrySelector, initialCountryCode]);

    const replaceUrlParams = useCallback((updates: Record<string, string | null | undefined>) => {
    if (!isBrowser) return;
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    });
  }, [setSearchParams]);

  const handleCountryChange = useCallback((country: CountryItem | undefined) => {
    isUserChangingCountryRef.current = true;
    setCountry(country);
    setPageafter(undefined);
    setShowWelcomeDialog(false);

    if (isBrowser) {
      setWelcomeDismissed(true);
      setStoredCountryCode(country?.code ?? null);
    }

    replaceUrlParams({ countrySelector: null });
    forceCountrySelectorRef.current = false;
    setTimeout(() => {
      isUserChangingCountryRef.current = false;
    }, 0);
  }, [replaceUrlParams]);

  const handleWelcomeDismiss = () => {
    if (!isBrowser) return;
    setWelcomeDismissed(true);
    setShowWelcomeDialog(false);
    replaceUrlParams({ countrySelector: null });
    forceCountrySelectorRef.current = false;
  };

  const handlePlatformChange = (platform: AudiencePlatformItem | undefined) => {
    setPlatform(platform);
    setPageafter(undefined);
    if (isBrowser) {
      setStoredPlatformCode(platform?.code ?? null);
    }
  };

    const introSubtitle = useMemo(() => {
    if (!country) {
      return 'Pick a country to explore category-level audiences and their descriptions.';
    }

    return `Audience catalogue for ${country.display_name}. Browse categories to learn how Digiseg defines each segment.`;
  }, [country]);

  useEffect(() => {
    return () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect();
        loadMoreObserverRef.current = null;
      }
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

    useEffect(() => {
    if (!isBrowser) return;
    if (country && !forceCountrySelectorRef.current) {
      setWelcomeDismissed(true);
      setShowWelcomeDialog(false);
      replaceUrlParams({ countrySelector: null });
    }
  }, [replaceUrlParams, country]);


  useEffect(() => {
    if (!isBrowser || !platforms.length) return;

    const urlPlatformCode = searchParams.get('platform');
    const prevUrlPlatform = prevUrlPlatformRef.current;
    prevUrlPlatformRef.current = urlPlatformCode;

    if (urlPlatformCode) {
      const match = platforms.find((p) => p.code === urlPlatformCode);
      if (!match) {
        setPlatform(undefined);
        return;
      }
      setPlatform((prev) => (prev?.code === match.code ? prev : match));
      hasAppliedInitialPlatform.current = true;
      return;
    }

    if (prevUrlPlatform) {
      setPlatform(undefined);
      return;
    }

    if (!hasAppliedInitialPlatform.current) {
      const saved = getStoredPlatformCode();
      if (saved) {
        const match = platforms.find((p) => p.code === saved);
        if (match) {
          setPlatform(match);
        }
      }
      hasAppliedInitialPlatform.current = true;
    }
  }, [platforms, searchParams]);

  useEffect(() => {
    if (!isBrowser || !countries.length) return;

    if (isUserChangingCountryRef.current) return;

    const prevUrlCountry = prevUrlCountryRef.current;
    prevUrlCountryRef.current = urlCountryCode ?? null;

    const effectiveCountryCode =
      urlCountryCode ?? (!hasAppliedInitialCountry.current ? pathCountry : undefined);

    if (effectiveCountryCode) {
      const match = countries.find((c) => c.code.toUpperCase() === effectiveCountryCode);
      if (!match) {
        if (country) setCountry(undefined);
        return;
      }
      setCountry((prev) => (prev?.code === match.code ? prev : match));
      hasAppliedInitialCountry.current = true;
      setStoredCountryCode(match.code);
      setWelcomeDismissed(true);
      setShowWelcomeDialog(false);
      forceCountrySelectorRef.current = false;
      return;
    }

    if (prevUrlCountry) {
      if (country) setCountry(undefined);
      return;
    }

    if (!hasAppliedInitialCountry.current) {
      const saved = getStoredCountryCode();
      if (saved) {
        const match = countries.find((c) => c.code === saved);
        if (match) {
          setCountry(match);
        }
      }
      hasAppliedInitialCountry.current = true;
    }
  }, [countries, urlCountryCode, country, pathCountry]);

  useEffect(() => {
    if (!countries.length || !platforms.length) return;
    const isRealPlatform = !!platform && platforms.some((p) => p.code === platform.code);
    replaceUrlParams({
      country: country ? country.code : undefined,
      platform: isRealPlatform ? platform!.code : undefined,
      countrySelector: showWelcomeDialog ? 'open' : null,
      category: activeCategoryCode ?? null,
      audience: openAudienceCode ?? null,
      filter: filter || null,
      pagesize: pagesize.toString(),
      labels: audienceViewMode.length > 0 ? audienceViewMode.join(',') : null,
      sort: sort || 'asc',
      pageafter: null,
    });
  }, [
    replaceUrlParams,
    country,
    platform,
    showWelcomeDialog,
    activeCategoryCode,
    openAudienceCode,
    filter,
    pagesize,
    audienceViewMode,
    sort,
    countries.length,
    platforms.length,
  ]);

  const handleCategorySelect = (categoryCode: string) => {
  setActiveCategoryCode(prev => (prev === categoryCode ? null : categoryCode));
  setPageafter(undefined);
};

  const handleViewModeChange = (mode: AudienceViewMode) => {
    setAudienceViewMode(prev => 
      JSON.stringify(prev) === JSON.stringify(mode) ? [] : mode
    );
    setPageafter(undefined);
  };

    const handleOpenCountrySelector = () => {
    setShowWelcomeDialog(true);
    replaceUrlParams({ countrySelector: 'open' });
    forceCountrySelectorRef.current = true;
  };

  const searchState: SearchState = { searchQuery, onSearchChange: setSearchQuery };
  const dialogState: DialogState = { showWelcomeDialog, onWelcomeDismiss: handleWelcomeDismiss, onOpenCountrySelector: handleOpenCountrySelector };
  const categoryState: CategoryState = { activeCategoryCode, openAudienceCode, activeCategory, onCategorySelect: handleCategorySelect, onClearCategory: clearCategoryFilter, onAudienceModalChange: handleAudienceModalOpenChange };
  const countryStateWithHandlers: CountryState = { ...countryState, onCountryChange: handleCountryChange, onOpenCountrySelector: handleOpenCountrySelector };
  const platformStateWithHandlers: PlatformState = { ...platformState, onPlatformChange: handlePlatformChange };
  const layoutState: LayoutState = { isCompact, isMedium };

  return (
    <>
      {shouldRenderStandaloneModal && (
        <AudienceRecommendationModal
          audienceItem={resolvedAudience}
          audienceCode={openAudienceCode!}
          // categoryName={resolvedAudience?.category?.display_name || ''}
          countries={countries}
          platforms={platforms}
          selectedCountry={country}
          selectedPlatform={platform}
          onCountrySelected={handleCountryChange}
          onPlatformSelected={handlePlatformChange}
          open
          onOpenChange={(openState) => {
            handleAudienceModalOpenChange(openAudienceCode!, openState);
          }}
          shareControls={resolvedAudience ? (
            <ShareMenu
              target={{
                type: 'audience',
                code: resolvedAudience.code,
                name: resolvedAudience.display_name,
                categoryCode: resolvedAudience.category?.code,
              }}
              selectedCountry={country}
              selectedPlatform={platform}
              buildShareUrl={buildShareUrl}
            />
          ) : undefined}
        />
      )}
      <AudienceStickyHeader
        countryState={countryStateWithHandlers}
        platformState={platformStateWithHandlers}
        searchState={searchState}
        layoutState={layoutState}
        visible={showStickyHeader}
        audienceViewMode={audienceViewMode}
        onViewModeChange={handleViewModeChange}
        hasAudiences={hasVisibleAudiences}
        />
      <Box px={{ initial: '4', md: '6' }} py="6" className="audience-page-shell">
      <AudienceHeroSection
        audienceViewMode={audienceViewMode}
        onViewModeChange={handleViewModeChange}
        ref={headerSentinelRef}
        onWelcomeDismiss={handleWelcomeDismiss}
        countryState={countryStateWithHandlers}
        platformState={platformStateWithHandlers}
        searchState={searchState}
        dialogState={dialogState}
        categoryState={categoryState}
        introSubtitle={introSubtitle}
        isSearching={isSearching}
        hasAudiences={hasVisibleAudiences}
      />
      {showStickyHeader && (
        <Box className="go-to-top-wrapper">
          <GoToTopButton visible={showStickyHeader} onClick={handleGoToTop} />
        </Box>
      )}
      <Box>
      <AudienceCategoriesView
        categories={renderedCategories}
        isLoading={isLoading}
        shouldShowInitialSkeleton={shouldShowInitialSkeleton}
        countryState={countryStateWithHandlers}
        platformState={platformStateWithHandlers}
        categoryState={categoryState}
        paginationState={{
          rangeTruncated: !activeCategoryCode && !!meta?.page?.rangeTruncated,
        }}
        buildShareUrl={buildShareUrl}
        categoryRefs={categoryRefs}
        loadMoreSentinelRef={loadMoreSentinelRef}
      />
      </Box>
    </Box>
    </>
  );
};

export default AudienceListApp;
